import BaseObject, { globalOnChange } from "./models/BaseObject";
import WatchableValue from "./models/WatchableValue";
import type { State } from "./State";
import type BaseModel from "./models/BaseModel";
import type Channel from "./models/Channel";
import type { C1_State } from "../ConceptOne/src/model/C1_State";
import type { EditMode } from "../ConceptOne/src/types/C1_AppTypes";
import type { C1_BaseObject } from "../ConceptOne/src/model/C1_BaseObject";
import C1_EloItem from "../ConceptOne/src/model/C1_EloItem";
import { C1_Button } from "../ConceptOne/src/model/C1_Button";
import { C1_Consumer } from "../ConceptOne/src/model/C1_Consumer";
import { C1_Furniture } from "../ConceptOne/src/model/C1_Furniture";
import { C1_Room } from "../ConceptOne/src/model/C1_Room";
import { C1_Opening } from "../ConceptOne/src/model/C1_Opening";
import { dockGroupForSelection } from "../ConceptOne/src/dockingArea/C1_dockSelection";
import { hasButtonEditorContent, hasConsumerEditorContent } from "../ConceptOne/src/dockingArea/C1_editorAvailability";
import { isLightGroupDefinitionId } from "../ConceptOne/src/furniture/C1_lightGroup";
import { getOpeningEloProfile } from "../ConceptOne/src/data/C1_OpeningEloDefinitions";
import { getEloItemProfileForInstance, getRoomEloItemProfile } from "../ConceptOne/src/data/C1_eloItemsRegistry";

export type DockVisibility = "collapsed" | "hidden" | "visible";
export type DockPanel<T> = {
  readonly visibility: WatchableValue<DockVisibility>;
  readonly content: WatchableValue<T | undefined>;
};
export type ActiveEloItemEditor = { type: "button"; item: C1_Button } | { type: "light"; item: C1_Consumer };

const electricalOnlyFurniture = new Set([
  "electricalButton", "electricalLight", "electricalOutlet", "fixedConsumer", "fixedConsumer400",
  "outdoorLight", "outdoorOutletGroup", "outdoorButton",
]);

/** Owns dock contents and visibility. Views only render these observable decisions. */
export default class DockManager extends BaseObject {
  override get className(): string { return "DockManager"; }

  readonly palette = this.panel<EditMode>();
  readonly groupEditor = this.panel<C1_BaseObject<any>>();
  readonly buttonEditor = this.panel<C1_Button>();
  readonly lightEditor = this.panel<C1_Consumer>();
  readonly consumerEditor = this.lightEditor;
  readonly modeEditor = this.panel<C1_BaseObject<any>>();
  readonly view = this.panel<C1_State>();
  readonly settings = this.panel<C1_State>();
  readonly masterPdf = this.panel<C1_State>();

  // Compatibility for plan graphics that highlight an explicitly opened editor.
  readonly buttonEditDialogItemId = new WatchableValue<string | null>(null);
  readonly lightEditDialogItemId = new WatchableValue<string | null>(null);
  readonly modeEditorClosedForSelectionId = new WatchableValue<string | null>(null);
  private model?: C1_State;
  private keys = new Map<object, string>();
  private updating = false;
  private lastSelection?: C1_BaseObject<any>;
  private lastScopeAndMode?: string;
  private requestedButton?: C1_Button;
  private requestedLight?: C1_Consumer;
  private requestedButtonExplicit = false;
  private requestedConsumerExplicit = false;
  private buttonEditorClosed = false;
  private consumerEditorClosed = false;
  private lastItemType: "button" | "light" = "button";
  private explicitGroup?: { object: C1_BaseObject<any>; key: string };
  private activationKey(): string {
    return `${this.model?.project.id}:${this.model?.currentFloorId.value}:${this.model?.editMode.value}:${this.model?.selection?.id ?? ""}`;
  }
  private context = { visible: false, planOnly: false, roombookOpen: false };
  private disposers: (() => void)[] = [];
  private navigatorSelection?: { object: BaseModel; scope: string; timer?: ReturnType<typeof setTimeout> };
  private selectingFromNavigator = false;

  private navigatorSelectionScope(): string {
    return `${this.model?.project.id}:${this.model?.currentFloorId.value}:${this.model?.editMode.value}:${this.context.visible}:${this.context.roombookOpen}`;
  }

  cancelNavigatorSelection(): void {
    if (this.navigatorSelection?.timer !== undefined) clearTimeout(this.navigatorSelection.timer);
    this.navigatorSelection = undefined;
  }

  /** Highlight immediately; settle dock chapters and automatic editors after keyboard navigation pauses. */
  selectFromNavigator(object: BaseModel, onSettled?: () => void): void {
    this.cancelNavigatorSelection();
    const pending = { object, scope: this.navigatorSelectionScope(), timer: undefined as ReturnType<typeof setTimeout> | undefined };
    this.navigatorSelection = pending;
    this.selectingFromNavigator = true;
    try { this.app.selectionManager.setSelectedObjectWithoutNavigatorUpdate(object); }
    finally { this.selectingFromNavigator = false; }
    pending.timer = setTimeout(() => {
      if (this.navigatorSelection !== pending) return;
      const valid = !object.isDeleted && this.app.selectionManager.selectedObject === object
        && pending.scope === this.navigatorSelectionScope();
      this.cancelNavigatorSelection();
      if (valid) onSettled?.();
      this.updateEditors();
    }, 500);
  }

  constructor(private readonly app: State) {
    super({ storeInObjectRegistry: false, isPersistent: false });
  }

  private panel<T>(): DockPanel<T> {
    const panel = { visibility: new WatchableValue<DockVisibility>("hidden"), content: new WatchableValue<T | undefined>(undefined) };
    panel.visibility.onChange.subscribe(() => this.raiseOnUpdateUi());
    panel.content.onChange.subscribe(() => this.raiseOnUpdateUi());
    return panel;
  }

  /** Called after lazy C1 construction; never forces C1 creation during State field initialization. */
  bind(c1: C1_State): void {
    this.cancelNavigatorSelection();
    this.disposers.forEach(dispose => dispose());
    this.disposers = [];
    this.model = c1;
    const observe = (object: BaseObject) => {
      const token = object.onChange.subscribe(() => this.updateEditors());
      this.disposers.push(() => object.onChange.unsubscribe(token));
    };
    [c1, c1.project, c1.project.displayMode, this.app.selectionManager,
      this.app.selectionManager.displaySelections, this.app.selectionManager.editorPins,
      this.buttonEditDialogItemId, this.lightEditDialogItemId, this.modeEditorClosedForSelectionId,
      this.app.undoManager].forEach(observe);
    const token = globalOnChange.subscribe((object: BaseObject) => {
      const owners = [c1.selection?.owner, this.groupEditor.content.value?.owner,
        this.buttonEditor.content.value?.owner, this.lightEditor.content.value?.owner];
      if (owners.some(owner => {
        const seen = new Set<BaseObject>();
        for (let current: BaseObject | undefined = owner; current && !seen.has(current); current = current.parent) {
          if (object === current) return true;
          seen.add(current);
        }
        return false;
      })) this.updateEditors();
    });
    this.disposers.push(() => globalOnChange.unsubscribe(token));
    this.updateEditors();
  }

  openSettings(): void {
    this.settings.content.value = this.model;
    this.settings.visibility.value = "visible";
  }
  closeSettings(): void { this.close(this.settings); }
  openMasterPdf(): void {
    this.masterPdf.content.value = this.model;
    this.masterPdf.visibility.value = "visible";
  }
  closeMasterPdf(): void { this.close(this.masterPdf); }

  setContext(context: typeof this.context): void {
    this.context = context;
    this.updateEditors();
  }

  selectMode(mode: EditMode): void {
    if (!this.model) return;
    this.model.editMode.value = mode;
    if (mode === "building") {
      this.model.roomDialogDocked.value = true;
      this.model.roomDialogOpen.value = true;
    } else this.model.furnitureDialogOpen.value = mode !== "interactionConcept";
    this.keys.delete(this.palette);
    this.updateEditors();
  }

  openPalette(mode = this.model?.editMode.value): void {
    if (!mode || !this.model) return;
    this.model.editMode.value = mode;
    if (mode === "building") {
      this.model.roomDialogDocked.value = true;
      this.model.roomDialogOpen.value = true;
    } else this.model.furnitureDialogOpen.value = true;
    this.palette.content.value = mode;
    this.collapse(this.palette, false);
  }
  closePalette(): void { this.close(this.palette); }
  private suppressStructuralEditor(object: C1_BaseObject<any> | undefined): boolean {
    return this.model?.editMode.value === "furnishing" && (object instanceof C1_Room || object instanceof C1_Opening);
  }
  openGroupEditor(object: C1_BaseObject<any>): void {
    this.cancelNavigatorSelection();
    if (this.suppressStructuralEditor(object)) return;
    this.explicitGroup = { object, key: this.activationKey() };
    this.groupEditor.content.value = object;
    this.groupEditor.visibility.value = "visible";
  }
  closeGroupEditor(): void { this.closeDockEditor(this.groupEditor); }
  openView(): void { this.view.visibility.value = "visible"; }
  closeView(): void { this.close(this.view); }
  collapse<T>(panel: DockPanel<T>, collapsed = true): void {
    if (panel === this.palette && !collapsed && panel.visibility.value !== "visible" && this.isElectricalDock()) {
      for (const below of [this.groupEditor, this.buttonEditor, this.consumerEditor, this.view]) {
        if (below.visibility.value === "visible") below.visibility.value = "collapsed";
      }
    }
    panel.visibility.value = collapsed ? "collapsed" : "visible";
  }
  private isElectricalDock(): boolean {
    return this.context.visible && !this.context.roombookOpen
      && ["EloConcept", "EloDetail", "interactionConcept"].includes(this.model?.editMode.value ?? "");
  }
  /** Reopen the nearest remaining section above an explicitly closed editor. */
  private closeDockEditor<T>(panel: DockPanel<T>, wasOpen = panel.visibility.value !== "hidden"): void {
    this.close(panel);
    if (!wasOpen || this.updating || !this.isElectricalDock()) return;
    const sections: DockPanel<any>[] = [this.palette, this.groupEditor, this.buttonEditor, this.consumerEditor];
    for (let index = sections.indexOf(panel) - 1; index >= 0; index--) {
      const above = sections[index];
      if (above.visibility.value === "hidden") continue;
      this.collapse(above, false);
      break;
    }
  }
  /** Divider gestures use the same observable expand/collapse state as the headers. */
  collapseSection(id: string, collapsed = true): void {
    if (id === "c1-dock-palette") this.collapse(this.palette, collapsed);
    else if (id === "c1-dock-group") this.collapse(this.groupEditor, collapsed);
    else if (id === "c1-dock-item") this.collapse(this.buttonEditor, collapsed);
    else if (id === "c1-dock-consumer") this.collapse(this.consumerEditor, collapsed);
  }
  close<T>(panel: DockPanel<T>): void { panel.visibility.value = "hidden"; }

  openButtonEditor(button: C1_Button, pin = false): void {
    this.cancelNavigatorSelection();
    if (!this.model || button.owner?.isDeleted || !hasButtonEditorContent(button)) return;
    this.modeEditorClosedForSelectionId.value = null;
    this.buttonEditorClosed = false;
    this.lastItemType = "button";
    this.requestedButton = button;
    this.requestedButtonExplicit = true;
    this.buttonEditor.content.value = button;
    this.buttonEditDialogItemId.value = button.id;
    this.app.selectionManager.editorPins.remove("eloEditor");
    if (button.owner) {
      this.app.selectionManager.displaySelections.set("buttonEditor", button.owner);
      if (pin) this.app.selectionManager.editorPins.set("eloEditor", button.owner);
    }
    this.updateEditors();
    if (this.buttonEditor.content.value && this.context.visible) this.buttonEditor.visibility.value = "visible";
    this.collapsePaletteForItem(button);
  }
  openLightEditor(light: C1_Consumer): void {
    this.cancelNavigatorSelection();
    if (!this.model || light.owner?.isDeleted || !hasConsumerEditorContent(light)) return;
    this.modeEditorClosedForSelectionId.value = null;
    this.consumerEditorClosed = false;
    this.lastItemType = "light";
    this.requestedLight = light;
    this.requestedConsumerExplicit = true;
    this.lightEditor.content.value = light;
    this.lightEditDialogItemId.value = light.id;
    this.updateEditors();
    if (this.lightEditor.content.value && this.context.visible) this.lightEditor.visibility.value = "visible";
    this.collapsePaletteForItem(light);
  }
  handleItemSelection(item: C1_EloItem): void {
    const mode = this.model?.editMode.value;
    if (item instanceof C1_Button && mode !== "EloDetail" && mode !== "interactionConcept") this.openButtonEditor(item);
    if (item instanceof C1_Consumer) this.openLightEditor(item);
  }
  closeButtonEditor(): void {
    const wasOpen = this.buttonEditor.visibility.value !== "hidden";
    this.buttonEditorClosed = true;
    this.requestedButton = undefined;
    this.requestedButtonExplicit = false;
    this.app.selectionManager.editorPins.remove("eloEditor");
    this.app.selectionManager.displaySelections.remove("buttonEditor");
    this.buttonEditDialogItemId.value = null;
    this.closeDockEditor(this.buttonEditor, wasOpen);
  }
  closeLightEditor(): void {
    const wasOpen = this.consumerEditor.visibility.value !== "hidden";
    this.consumerEditorClosed = true;
    this.requestedLight = undefined;
    this.requestedConsumerExplicit = false;
    this.app.selectionManager.editorPins.remove("consumerEditor");
    this.lightEditDialogItemId.value = null;
    this.closeDockEditor(this.consumerEditor, wasOpen);
  }
  closeItemEditor(): void {
    this.modeEditorClosedForSelectionId.value = this.model?.selection?.id ?? "";
    this.closeButtonEditor();
    this.closeLightEditor();
    this.close(this.modeEditor);
  }
  reopenSelection(): void {
    if (!this.selectingFromNavigator) this.cancelNavigatorSelection();
    this.modeEditorClosedForSelectionId.value = null;
    if (this.model?.selection instanceof C1_Button) this.buttonEditorClosed = false;
    if (this.model?.selection instanceof C1_Consumer) this.consumerEditorClosed = false;
    this.keys.delete(this.groupEditor);
    this.keys.delete(this.buttonEditor);
    this.keys.delete(this.lightEditor);
    this.updateEditors();
  }
  setItemPinned(item: C1_Button | C1_Consumer, pinned: boolean): void {
    const slot = item instanceof C1_Consumer ? "consumerEditor" : "eloEditor";
    if (pinned && item.owner) this.app.selectionManager.editorPins.set(slot, item.owner as BaseModel);
    else {
      this.app.selectionManager.editorPins.remove(slot);
      if (this.model?.selection !== item) {
        if (this.buttonEditDialogItemId.value === item.id) this.buttonEditDialogItemId.value = null;
        if (this.lightEditDialogItemId.value === item.id) this.lightEditDialogItemId.value = null;
      }
    }
    this.updateEditors();
  }
  get activeItemEditor(): ActiveEloItemEditor | undefined {
    if (this.lastItemType === "light" && this.lightEditor.content.value && this.lightEditor.visibility.value !== "hidden")
      return { type: "light", item: this.lightEditor.content.value };
    if (this.buttonEditor.content.value && this.buttonEditor.visibility.value !== "hidden")
      return { type: "button", item: this.buttonEditor.content.value };
    if (this.lightEditor.content.value && this.lightEditor.visibility.value !== "hidden")
      return { type: "light", item: this.lightEditor.content.value };
    return undefined;
  }

  /** SelectionManager retains navigator paths; editor retention policy lives here. */
  updateDisplaySelections(object: BaseModel | undefined, preserveButtonEditor = false): void {
    const c1 = this.app.c1;
    const manager = this.app.selectionManager;
    const electricalMode = ["EloConcept", "EloDetail", "interactionConcept"].includes(c1.editMode.value);
    const pinnedButton = manager.editorPins.get("eloEditor");
    const button = object?.className === "Button" ? object
      : object?.isButtonChannel ? (object as Channel).parentDevice : undefined;
    const keepOpen = electricalMode && (pinnedButton?.className === "Button" || this.buttonEditDialogItemId.value != null);
    preserveButtonEditor ||= keepOpen;
    if (object?.className === "VirtualConnection") return;
    if (!object) {
      manager.displaySelections.clear(preserveButtonEditor ? ["buttonEditor"] : []);
      return;
    }
    manager.displaySelections.addNavigatorObject(object);
    if (button?.className === "Button") {
      manager.displaySelections.set("buttonEditor", button);
      if (keepOpen) {
        this.buttonEditDialogItemId.value = button.c1?.id ?? button.id;
        if (pinnedButton?.className === "Button") manager.editorPins.set("eloEditor", button);
      }
    } else if (object.className !== "Connection" && !preserveButtonEditor) manager.displaySelections.remove("buttonEditor");
  }

  /** Gleicher Schlüssel: Einklappen/Schließen beibehalten. Neuer Schlüssel: Editor erneut aktivieren. */
  private assign<T>(panel: DockPanel<T>, content: T | undefined, key: string, collapsed = false, enabled = true): void {
    panel.content.value = content;
    if (content === undefined || !enabled) {
      panel.visibility.value = "hidden";
      this.keys.set(panel, "");
      return;
    }
    if (this.keys.get(panel) !== key) {
      panel.visibility.value = collapsed ? "collapsed" : "visible";
    }
    this.keys.set(panel, key);
  }

  /** Aktualisiert die Dock-Fenster. Die Regeln jedes Editors stehen in einer eigenen Methode. */
  updateEditors(): void {
    const c1 = this.model;
    if (!c1 || this.updating) return;
    if (this.navigatorSelection) {
      if (this.selectingFromNavigator || (this.app.selectionManager.selectedObject === this.navigatorSelection.object
        && this.navigatorSelection.scope === this.navigatorSelectionScope())) return;
      this.cancelNavigatorSelection();
    }
    this.updating = true;
    try {
      const selection = c1.selection?.owner?.isDeleted ? undefined : c1.selection;
      const mode = c1.editMode.value;
      const scope = `${c1.project.id}:${c1.currentFloorId.value}`;
      const key = `${scope}:${mode}:${selection?.id ?? ""}`;
      const dockVisible = this.context.visible && !this.context.roombookOpen;
      const scopeAndMode = `${scope}:${mode}`;
      const automatic = ["EloConcept", "EloDetail", "interactionConcept"].includes(mode) && !this.context.roombookOpen;
      const roomForButtonEditor = this.isRoomSelectionForButtonEditor(selection);

      const modeEditorClosed = this.updateSelectionState(selection, scopeAndMode);
      this.updatePalette(c1, mode, scopeAndMode, dockVisible);
      if (!roomForButtonEditor) this.updateGroupEditor(selection, key, dockVisible);
      this.assign(this.view, c1, scope, false, dockVisible);
      this.updateButtonEditor(c1, selection, scopeAndMode, automatic);
      this.updateConsumerEditor(c1, selection, scopeAndMode, automatic);
      if (!roomForButtonEditor) this.updateModeEditor(c1, selection, key, dockVisible && !modeEditorClosed);
    } finally { this.updating = false; }
  }

  /** Bei offenem Taster dient die Raumauswahl nur als Quelle der Verbraucher-Liste. */
  private isRoomSelectionForButtonEditor(selection: C1_BaseObject<any> | undefined): boolean {
    return selection instanceof C1_Room && this.buttonEditor.visibility.value === "visible"
      && !!this.buttonEditor.content.value;
  }

  /** Aktualisiert Auswahlwechsel und liefert, ob der Modus-Editor geschlossen bleiben soll. */
  private updateSelectionState(selection: C1_BaseObject<any> | undefined, scopeAndMode: string): boolean {
    if (this.lastScopeAndMode !== undefined && this.lastScopeAndMode !== scopeAndMode) {
      this.modeEditorClosedForSelectionId.value = null;
      this.buttonEditorClosed = false;
      this.consumerEditorClosed = false;
    }
    this.lastScopeAndMode = scopeAndMode;

    const closedFor = this.modeEditorClosedForSelectionId.value;
    if (closedFor !== null && selection && selection.id !== closedFor) {
      this.modeEditorClosedForSelectionId.value = null;
    }
    const closed = this.modeEditorClosedForSelectionId.value === (selection?.id ?? "");
    if (selection === this.lastSelection) return closed;
    this.lastSelection = selection;

    // Direkte Raum-/Gruppenauswahl räumt die Item-Editoren frei.
    // Leuchtengruppen vertreten einen Consumer und öffnen dessen Item-Editor.
    if (selection && dockGroupForSelection(selection) === selection && !this.consumerForSelection(selection)
      && !this.isRoomSelectionForButtonEditor(selection)) {
      this.closeButtonEditor();
      this.closeLightEditor();
    }

    if (selection instanceof C1_Button) {
      this.buttonEditorClosed = false;
      this.lastItemType = "button";
      this.requestedButton = selection;
      this.requestedButtonExplicit = false;
      this.buttonEditDialogItemId.value = selection.id;
    }
    const consumer = this.consumerForSelection(selection);
    if (consumer) {
      this.consumerEditorClosed = false;
      this.lastItemType = "light";
      this.requestedLight = consumer;
      this.requestedConsumerExplicit = false;
      this.lightEditDialogItemId.value = consumer.id;
    }
    return closed;
  }

  private consumerForSelection(selection: C1_BaseObject<any> | undefined): C1_Consumer | undefined {
    if (selection instanceof C1_Consumer) return selection;
    if (selection instanceof C1_Furniture && isLightGroupDefinitionId(selection.definitionId)) {
      return selection.lightConsumer;
    }
    return undefined;
  }

  private usableItem(item: C1_BaseObject<any> | undefined): C1_BaseObject<any> | undefined {
    if (item?.owner?.isDeleted) return undefined;
    return item;
  }

  /** Hinzufügen-Palette: im Interaktionsmodus zunächst eingeklappt; im Raumbuch ausgeblendet. */
  private updatePalette(c1: C1_State, mode: EditMode, key: string, visible: boolean): void {
    this.assign(this.palette, mode, key, mode === "interactionConcept", visible);
    if (visible && mode === "building") {
      c1.roomDialogDocked.value = true;
      c1.roomDialogOpen.value = true;
    }
  }

  /** Bei Elo-Auswahl nur bereits angezeigte Eigentümer behalten, niemals automatisch öffnen. */
  private retainOwnerEditor(panel: DockPanel<C1_BaseObject<any>>, item: C1_EloItem, key: string, visible: boolean): void {
    const existing = this.usableItem(panel.content.value);
    const seen = new Set<C1_BaseObject<any>>();
    for (let owner = item.parent; owner && !seen.has(owner); owner = owner.parent) {
      if (owner === existing && panel.visibility.value !== "hidden" && visible && !this.suppressStructuralEditor(owner)) {
        this.keys.set(panel, key);
        return;
      }
      seen.add(owner);
    }
    this.assign(panel, undefined, key);
  }

  /** Objekt-/Gruppeneditor: direkte Auswahl öffnen; bei Eloitems nur offene Eigentümer behalten. */
  private updateGroupEditor(selection: C1_BaseObject<any> | undefined, key: string, visible: boolean): void {
    if (this.explicitGroup?.key !== key) this.explicitGroup = undefined;
    if (selection instanceof C1_EloItem && !this.explicitGroup) {
      this.retainOwnerEditor(this.groupEditor, selection, key, visible);
      return;
    }
    let group = this.explicitGroup?.object ?? dockGroupForSelection(selection);
    if (this.suppressStructuralEditor(group)) group = undefined;
    group = this.usableItem(group);
    const collapsed = !this.explicitGroup && selection !== group;
    this.assign(this.groupEditor, group, key, collapsed, visible);
  }

  /** Taster-/Sensor-Editor: Pin, explizite Öffnung, Auswahl, Navigator, bisheriger Inhalt. */
  private updateButtonEditor(c1: C1_State, selection: C1_BaseObject<any> | undefined, key: string, automatic: boolean): void {
    const pinned = this.usableItem(this.app.selectionManager.editorPins.get("eloEditor")?.c1);
    const displayed = this.usableItem(this.app.selectionManager.displaySelections.get("buttonEditor")?.c1);
    const explicit = this.usableItem(this.requestedButton?.id === this.buttonEditDialogItemId.value
      ? this.requestedButton : c1.objectRegistry.getObject<C1_Button>(this.buttonEditDialogItemId.value ?? undefined));

    let candidate: C1_BaseObject<any> | undefined;
    if (pinned instanceof C1_Button) {
      candidate = pinned;
    } else if (explicit) {
      candidate = explicit;
    } else if (automatic && selection instanceof C1_Button) {
      candidate = selection;
    } else if (automatic && displayed) {
      candidate = displayed;
    } else {
      candidate = this.usableItem(this.buttonEditor.content.value);
    }

    let button: C1_Button | undefined;
    if (!this.buttonEditorClosed && candidate instanceof C1_Button && hasButtonEditorContent(candidate)) {
      button = candidate;
    }
    const explicitlyOpened = !!explicit && this.requestedButtonExplicit;
    const visible = this.context.visible && (explicitlyOpened || !!pinned || automatic);
    // Die Auswahl eines Consumers darf den eingeklappten Zustand dieses Editors nicht verändern.
    this.assignItemEditor(this.buttonEditor, button, `${key}:${button?.id ?? ""}`, visible);
  }

  /** Consumer-Editor: unabhängig vom Taster-Editor; auch eine Leuchtengruppe liefert ihren Consumer. */
  private updateConsumerEditor(c1: C1_State, selection: C1_BaseObject<any> | undefined, key: string, automatic: boolean): void {
    const pinned = this.usableItem(this.app.selectionManager.editorPins.get("consumerEditor")?.c1);
    const explicit = this.usableItem(this.requestedLight?.id === this.lightEditDialogItemId.value
      ? this.requestedLight : c1.objectRegistry.getObject<C1_Consumer>(this.lightEditDialogItemId.value ?? undefined));

    const selected = automatic ? this.consumerForSelection(selection) : undefined;
    let candidate: C1_BaseObject<any> | undefined;
    if (pinned instanceof C1_Consumer) {
      candidate = pinned;
    } else if (explicit) {
      candidate = explicit;
    } else if (selected) {
      candidate = selected;
    } else {
      candidate = this.usableItem(this.consumerEditor.content.value);
    }

    let consumer: C1_Consumer | undefined;
    if (!this.consumerEditorClosed && candidate instanceof C1_Consumer && hasConsumerEditorContent(candidate)) {
      consumer = candidate;
    }
    const explicitlyOpened = !!explicit && this.requestedConsumerExplicit;
    const visible = this.context.visible && (explicitlyOpened || !!pinned || automatic);
    this.assignItemEditor(this.consumerEditor, consumer, `${key}:${consumer?.id ?? ""}`, visible);
  }

  /** Ein neu geöffneter Item-Editor schafft Platz, außer unmittelbar nach dessen Platzierung. */
  private collapsePaletteForItem(item: C1_EloItem): void {
    const placed = this.model?.justPlacedObject.value;
    if (!this.context.visible || this.context.roombookOpen || placed === item || this.consumerForSelection(placed) === item) return;
    if (this.palette.visibility.value === "visible") this.collapse(this.palette);
  }

  private assignItemEditor<T extends C1_EloItem>(panel: DockPanel<T>, item: T | undefined, key: string, visible: boolean): void {
    const previousItem = panel.content.value;
    const previousVisibility = panel.visibility.value;
    this.assign(panel, item, key, false, visible);
    if (item && panel.visibility.value === "visible" && (previousItem !== item || previousVisibility !== "visible")) {
      this.collapsePaletteForItem(item);
    }
  }

  /** Modusabhängiger Editor: allgemeine Möbeleigenschaften und elektrische Ausstattung. */
  private updateModeEditor(c1: C1_State, selection: C1_BaseObject<any> | undefined, key: string, visible: boolean): void {
    if (selection instanceof C1_EloItem) {
      this.retainOwnerEditor(this.modeEditor, selection, key, visible);
      return;
    }
    const mode = c1.editMode.value;
    const electricalFurniture = selection instanceof C1_Furniture && electricalOnlyFurniture.has(selection.definitionId);
    const showElectrical = electricalFurniture || mode === "EloConcept" || mode === "EloDetail" ||
      (mode === "furnishing" && c1.project.displayMode.value !== "design");
    const hasGeneral = selection instanceof C1_Furniture && !electricalFurniture &&
      (mode === "furnishing" || isLightGroupDefinitionId(selection.definitionId));

    let hasElectrical = false;
    if (showElectrical && !this.suppressStructuralEditor(selection)) {
      if (selection instanceof C1_Room) {
        hasElectrical = mode === "EloConcept" || mode === "EloDetail" || getRoomEloItemProfile(selection.type).length > 0;
      } else if (selection instanceof C1_Opening) {
        hasElectrical = getOpeningEloProfile(selection).definitions.length > 0;
      } else if (selection instanceof C1_Furniture) {
        hasElectrical = getEloItemProfileForInstance(selection.definitionId, selection.eloTree).length > 0;
      }
    }
    const content = hasGeneral || hasElectrical ? selection : undefined;
    this.assign(this.modeEditor, content, key, false, visible);
  }
}
